


















































































import {Component, Mixins, Prop, Ref} from 'vue-property-decorator';
import JdsButton from "@/design-system/components/Button/JdsButton.vue"
import JdsCloseSymbol from "@/design-system/shared/JdsCloseSymbol.vue"
import anime from "animejs"
import TextProviderMixin from "@/design-system/utilities/TextProviderMixin"
import UniqueIdMixin from "@/design-system/utilities/UniqueIdMixin"
import JdsField from "@/design-system/components/Field/JdsField.vue"
import JdsListItem from "@/design-system/components/List/JdsListItem.vue"
import JdsListLayout from "@/design-system/components/List/JdsListLayout.vue"
import {AcquireFirstFocus} from "@/design-system/utilities/FocusHelper"
import JdsEntrySlot from "@/design-system/components/JdsEntrySlot.vue"
import JdsGlyph from "@/design-system/icons/JdsGlyph.vue"
import JdsToolbar from "@/design-system/patterns/JdsToolbar.vue"
import {ActiveProvide} from "@/support/reactive"
import JdsIcon from "@/design-system/icons/JdsIcon.vue";
import AnalyticsEmitterMixin from "@/design-system/foundation/AnalyticsProvider/AnalyticsEmitterMixin"
import JdhBreakdownCardlessContext from "@/design-system/patterns/Breakdown/JdhBreakdownCardlessContext.vue"
import { Portal } from '@linusborg/vue-simple-portal'
import { JdsError } from '@/design-system/utilities/JdsError';

/**
 * @status stable
 * @published 2.0.0
 * @changelog 2.0.0 Published.
 * @changelog 4.2.1 Make breakdowns in inspectors not show cards.
 * @changelog 4.6.0 Switch to simple-portal.
 * @changelog 5.9.2 Add noAutofocus property.
 * @changelog 6.8.0 Fix non-functioning close and cancel promise parameters and events.
 * @typed
 *
 * @analytics jds.inspector:cancel Fired on user-initiated inspector canceling.
 * @analyticsParam jds.inspector:cancel trigger Either `backdrop` or `button`, which the user clicked on to cancel this inspector.
 * @analyticsParam jds.inspector:cancel duration How long, in milliseconds, the inspector stayed open.
 */
@Component({
    components: {
        JdhBreakdownCardlessContext,
      JdsIcon,
        JdsToolbar,
        JdsGlyph,
        JdsListItem,
        JdsListLayout,
        JdsEntrySlot, JdsField, JdsButton, JdsCloseSymbol, SimplePortal: Portal}
})
export default class JdsInspector extends Mixins(TextProviderMixin, UniqueIdMixin, AnalyticsEmitterMixin) {

    @Prop() icon!: string;
    @Prop() noAutofocus!: boolean;

    @Ref() popupEl!: HTMLElement;
    @Ref() backsideEl!: HTMLElement;
    @Ref() backdropEl!: HTMLElement;
    state: boolean = false;
    isFlipped: boolean = false;
    openedAtTimestamp: number = Date.now();

    protected onClose?: (v?: unknown) => any;
    protected onCancel?: (v?: unknown) => any;
    protected lastResult?: 'close'|'cancel';

    @ActiveProvide('jds.breakdown.card') breakdownCard = false;


    keyListener(e: KeyboardEvent) {
        if (e.key === 'Escape' && !this.popupEl.contains(e.target as Node)) {
            this.cancel();
        }
    }

    /**
     * Open this inspector.
     *
     * @public
     */
    open() {
        document.addEventListener('keyup', this.keyListener);
        this.state = true;
        this.isFlipped = false;
        this.openedAtTimestamp = Date.now()


        return new Promise<unknown>((res, err) => {
            this.onClose = res;
            this.onCancel = () => {
                err(new JdsError('Inspector cancelled', (this.$options.name as string), 'cancel'));
            };
        });
    }

    acquireFocus() {
        if(!this.noAutofocus) {
            this.$nextTick(() => {
                AcquireFirstFocus(this.popupEl);
            });
        }
    }

    /**
     * Close this inspector; same effect as the `close` binding on the `toolbar` slot.
     *
     * @public
     */
    close() {
        this.lastResult = 'close';
        document.removeEventListener('keyup', this.keyListener);
        this.state = false;
        this.$emit('close');
        if (this.onClose) this.onClose();
    }

    /**
     * Close this inspector; same effect as the `cancel` binding on the `toolbar` slot. No analytics event will be
     * emitted.
     *
     * @public
     */
    cancel() {
        this.lastResult = 'cancel';
        document.removeEventListener('keyup', this.keyListener);
        this.state = false;
        this.$emit('cancel');
        if (this.onCancel) this.onCancel();
    }

    cancelViaBackdrop() {
        this.$analytics('jds.inspector:cancel', { trigger: 'backdrop', duration: Date.now() - this.openedAtTimestamp })
        this.cancel();
    }

    cancelViaButton() {
        this.$analytics('jds.inspector:cancel', { trigger: 'button', duration: Date.now() - this.openedAtTimestamp  })
        this.cancel();
    }

    /**
     * Reveal the opposite side of the inspector, populated by the `back` slot.
     *
     * @public
     * @experimental
     */
    flip() {
        this.isFlipped = true;

        this.$nextTick(() => {
            this.$nextTick(() => {
                AcquireFirstFocus(this.backsideEl);
            });
        });
    }

    enter(el: Node, done: ()=>void) {
        anime.timeline({
            easing: 'easeInOutExpo',
            complete: () => {
                try {
                    this.popupEl.style.removeProperty("height");
                } catch (e) {}
                done();
            },
        }).add({
            targets: this.popupEl,
            opacity: [0, 1],
            scale: [0.8, 1],
            translateY: ['15px', 0],
            duration: 250,
            easing: 'easeOutBack',
        }).add({
            targets: this.backdropEl,
            opacity: [0, 1],
            duration: 250,
            easing: 'easeOutExpo'
        }, 0)
    }

    leave(el: Node, done: ()=>void) {
        if (this.lastResult === 'cancel') {
            anime.timeline({
                complete: done,
            }).add({
                targets: this.isFlipped ? this.backsideEl : this.popupEl,
                opacity: ['100%', '0%'],
                scale: [1, 0.8],
                duration: 200,
                easing: 'easeInCubic',
                complete: done,
            }).add({
                targets: this.backdropEl,
                opacity: [1, 0],
                duration: 200,
                easing: 'linear'
            }, 0)
        } else {
            anime.timeline({
                complete: done,
            }).add({
                targets: this.isFlipped ? this.backsideEl : this.popupEl,
                opacity: ['100%', '0%'],
                scale: [1, 1.08],
                duration: 140,
                easing: 'easeInCubic',
                complete: done,
            }).add({
                targets: this.backdropEl,
                opacity: [1, 0],
                duration: 200,
                easing: 'linear'
            }, 0)
        }
    }

    flipEnter(el: Node, done: ()=>void) {
        anime.timeline({
            easing: 'easeInOutSine',
            complete: done,
        }).add({
            targets: el,
            // opacity: [0, 1],
            rotateY: ['-180deg', 0],
            duration: 500,
        })
    }

    flipLeave(el: Node, done: ()=>void) {
        anime.timeline({
            easing: 'easeInOutSine',
            complete: done,
        }).add({
            targets: el,
            // opacity: [1, 0],
            rotateY: [0, '180deg'],
            duration: 500,
        })
    }
}
